import { Event, Groups, Person } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import React, { useEffect, useMemo, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { ControllerRenderProps, FieldValues } from 'react-hook-form/dist/types';
import { IntlShape, useIntl } from 'react-intl';
import { addSignatureTask } from '../../api/activities-nim';
import { AddSignatureTaskUpdateDTO, EscalationTypeCode, TaskDTO } from '../../api/dto';
import { fetchEgroups, fetchMeetings, fetchPeopleByName } from '../../lib/api';
import { getEgroupLabel, getLovLabel, getPersonLabel } from '../../lib/label';
import { AsyncAutocompleteElement, FetchUseCase } from '../fields/AsyncAutocompleteElement';
import { TFormElement } from '../types';
import { UiSchemaType } from '../UiSchemaType';

export interface ConditionalEscalationType {
    label: string;
    value: EscalationTypeCode;
}

interface SignatureFormData {
    type: ConditionalEscalationType;
    value: any;
}

interface EntityConfig {
    fetchOptions: (searchTerm: string) => Promise<any[]>;
    getOptionLabel: (option: any) => string;
    equalityField: string;
    icon: JSX.Element;
    secondaryText: string;
    fetchUseCases: FetchUseCase[];
}

const createSignatureTypeOptions = (intl: IntlShape, isMeetingRequired = false): ConditionalEscalationType[] => [
    {
        label: intl.formatMessage({
            id: 'document.field.person',
        }),
        value: EscalationTypeCode.PERSON,
    },
    {
        label: intl.formatMessage({
            id: 'document.field.egroup',
        }),
        value: EscalationTypeCode.EGROUP,
    },
    ...(isMeetingRequired
        ? [
              {
                  label: intl.formatMessage({
                      id: 'document.field.meeting',
                  }),
                  value: EscalationTypeCode.MEETING,
              },
          ]
        : []),
];

export const SIGNATURE_TYPE_ICONS: Record<EscalationTypeCode, JSX.Element> = {
    [EscalationTypeCode.PERSON]: <Person />,
    [EscalationTypeCode.EGROUP]: <Groups />,
    [EscalationTypeCode.MEETING]: <Event />,
};

export const getSignatoryTypeIcon = (type: EscalationTypeCode): JSX.Element | undefined => {
    return SIGNATURE_TYPE_ICONS[type];
};

const createEntityConfig = (type: EscalationTypeCode, intl: IntlShape): EntityConfig | null => {
    const configs: Record<EscalationTypeCode, EntityConfig> = {
        [EscalationTypeCode.PERSON]: {
            fetchOptions: (searchTerm: string) => fetchPeopleByName(searchTerm, false),
            getOptionLabel: getPersonLabel,
            equalityField: 'id',
            icon: SIGNATURE_TYPE_ICONS[EscalationTypeCode.PERSON],
            secondaryText: intl.formatMessage({
                id: 'document.field.person',
            }),
            fetchUseCases: [FetchUseCase.ON_KEYSTROKE],
        },
        [EscalationTypeCode.EGROUP]: {
            fetchOptions: (searchTerm: string) => fetchEgroups(searchTerm),
            getOptionLabel: getEgroupLabel,
            equalityField: 'id',
            icon: SIGNATURE_TYPE_ICONS[EscalationTypeCode.EGROUP],
            secondaryText: intl.formatMessage({
                id: 'document.field.egroup',
            }),
            fetchUseCases: [FetchUseCase.ON_KEYSTROKE],
        },
        [EscalationTypeCode.MEETING]: {
            fetchOptions: (searchTerm: string) => fetchMeetings(searchTerm),
            getOptionLabel: getLovLabel,
            equalityField: 'code',
            icon: SIGNATURE_TYPE_ICONS[EscalationTypeCode.MEETING],
            secondaryText: intl.formatMessage({
                id: 'document.field.meeting',
            }),
            fetchUseCases: [FetchUseCase.ON_LOAD],
        },
    };
    return configs[type] || null;
};

const extractSignatureType = (selectedTypeRaw: any): EscalationTypeCode | null => {
    if (!selectedTypeRaw) return null;

    if (typeof selectedTypeRaw === 'object' && selectedTypeRaw?.value) {
        return selectedTypeRaw.value;
    }

    return selectedTypeRaw;
};

interface ConditionalEntitySelectorProps {
    disabled?: boolean;
    readOnly?: boolean;
    typePath: string;
    valuePath: string;
}

export const ConditionalEscalationSelector: React.FC<ConditionalEntitySelectorProps> = ({
    disabled = false,
    readOnly = false,
    typePath,
    valuePath,
}) => {
    const { watch, control, setValue } = useFormContext();
    const intl = useIntl();
    const selectedTypeRaw = watch(typePath);

    const selectedType = extractSignatureType(selectedTypeRaw);
    const signatureTypeOptions = useMemo(() => createSignatureTypeOptions(intl), [intl]);

    const config = useMemo(() => {
        return selectedType ? createEntityConfig(selectedType, intl) : null;
    }, [selectedType, intl]);

    const prevType = useRef(selectedType);
    useEffect(() => {
        if (selectedType !== prevType.current) {
            setValue(valuePath, null);
            prevType.current = selectedType;
        }
    }, [selectedType, setValue]);

    if (!selectedType) return null;

    if (!config) throw Error(`Unkown signature type ${selectedType}`);

    const renderOption = (optionProps: any, option: any) => (
        <li {...optionProps} style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center', flexShrink: 0 }}>{config.icon}</Box>
            <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                <Typography variant="body1" noWrap>
                    {config.getOptionLabel(option)}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                    {config.secondaryText}
                </Typography>
            </Box>
        </li>
    );

    const dynamicLabel = signatureTypeOptions.find((option) => option.value === selectedType)?.label || '';

    return (
        <Controller
            key={`${selectedType}-${Date.now()}`} // fixes dirty cache between autocomplete components in the same page
            name={valuePath}
            control={control}
            render={({ field, fieldState }) => (
                <AsyncAutocompleteElement
                    field={field as ControllerRenderProps<FieldValues, string>}
                    queryKey={['signature-entity', selectedType]}
                    fieldState={fieldState}
                    disabled={disabled}
                    readOnly={readOnly}
                    label={dynamicLabel}
                    multiple={false}
                    fetchOptions={config.fetchOptions}
                    fetchUseCases={config.fetchUseCases}
                    getOptionLabel={config.getOptionLabel}
                    autocompleteProps={{
                        fullWidth: true,
                        size: 'small',
                        isOptionEqualToValue: (option: any, value: any) =>
                            option?.[config.equalityField] === value?.[config.equalityField],
                        renderOption,
                    }}
                    textFieldProps={{
                        InputLabelProps: { shrink: true },
                        InputProps: { required: true },
                    }}
                />
            )}
        />
    );
};

export const getSignatureTypeOptionLabel = (
    option: ConditionalEscalationType | EscalationTypeCode,
    signatureTypeOptions: ConditionalEscalationType[]
): string => {
    if (typeof option === 'string') {
        return signatureTypeOptions.find((o) => o.value === option)?.label || option;
    }
    return option.label;
};

export const getConditionalEscalationTypeProperties = (intl: IntlShape, isMeetingRequired = true) => {
    const signatureTypeOptions = createSignatureTypeOptions(intl, isMeetingRequired);
    return {
        options: signatureTypeOptions,
        matchId: false,
        getOptionLabel: (option: ConditionalEscalationType | EscalationTypeCode) =>
            getSignatureTypeOptionLabel(option, signatureTypeOptions),
        autocompleteProps: {
            isOptionEqualToValue: (option: ConditionalEscalationType, value: ConditionalEscalationType) =>
                option.value === value.value,
            renderOption: (optionProps: any, option: ConditionalEscalationType) => (
                <li {...optionProps} style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        {getSignatoryTypeIcon(option.value)}
                    </Box>
                    <Typography variant="body1">{option.label}</Typography>
                </li>
            ),
        },
    };
};

export const addSignatureTaskSchema = (intl: IntlShape): TFormElement => ({
    type: UiSchemaType.LAYOUT_VERTICAL,
    elements: [
        {
            type: UiSchemaType.AUTOCOMPLETE,
            label: intl.formatMessage({ id: 'document.signature.select.type' }),
            path: 'extraData.type',
            required: true,
            typeProperties: getConditionalEscalationTypeProperties(intl),
        },
        {
            type: UiSchemaType.CUSTOM,
            Component: (props) => (
                <ConditionalEscalationSelector {...props} typePath="extraData.type" valuePath="extraData.value" />
            ),
        },
    ],
});

export const addSignatureTaskMutation = ({ task, extraData }: { task: TaskDTO; extraData: unknown }) => {
    if (!extraData || typeof extraData !== 'object') {
        throw new Error('Invalid extraData provided to addSignatureTaskMutation');
    }

    const data = extraData as SignatureFormData;

    const signaturePayload: AddSignatureTaskUpdateDTO = {
        taskId: task.id,
        type: data.type.value,
        person: null,
        egroup: null,
        meeting: null,
    };

    const signatureType = data.type.value;

    const payloadMapping: Record<
        EscalationTypeCode,
        keyof Pick<AddSignatureTaskUpdateDTO, 'person' | 'egroup' | 'meeting'>
    > = {
        [EscalationTypeCode.PERSON]: 'person',
        [EscalationTypeCode.EGROUP]: 'egroup',
        [EscalationTypeCode.MEETING]: 'meeting',
    };

    const payloadKey = payloadMapping[signatureType];
    if (payloadKey) {
        signaturePayload[payloadKey] = data.value;
    } else {
        throw new Error(`Unknown signature type: ${signatureType}`);
    }

    return addSignatureTask({
        id: task.documentId,
        signature: signaturePayload,
    });
};
